// Translations for Handbook sections. Keyed by section `id` → {title, subtitle, articles[]}.
// The Dutch source lives in Handbook.jsx; here we override title/subtitle/article
// strings when the user is in EN or ES. Missing keys fall back to Dutch.
//
// Each `articles` entry is `{ h, p }` where `p` may be a string or an array of strings,
// matching the shape used by Handbook.jsx renderer.

export const HANDBOOK_TRANSLATIONS = {
  en: {
    // --- Owner-only ---
    billing: {
      title: "Subscription & billing",
      subtitle: "Only the Eigenaar manages the subscription, invoices and payment details",
      articles: [
        { h: "What's included?", p: [
          "Rostr runs one subscription per company — all employees, team leads and managers share yours.",
          "Your team never sees prices, discounts or invoices: only the Eigenaar can open Subscription & billing.",
          "Rostr offers a free trial; after that you pick a plan that fits your team size.",
        ]},
        { h: "Our 4 plans (per month, in euros)", p: [
          "Starter — 2 to 15 employees · €12,99/mo",
          "Team — 16 to 40 employees · €29,99/mo (recommended for most hospitality and retail teams)",
          "Business — 41 to 100 employees · €59,99/mo",
          "Scale — 101 to 500 employees · €99,99/mo",
          "More than 500 employees or specific needs (SLA, extra roles, integrations)? Email rosterhelp@gmail.com for a tailored plan.",
          "You can upgrade or downgrade at any time — billing is automatically pro-rated.",
        ]},
        { h: "Where do I find the subscription?", p: "In the sidebar go to Settings → Subscription. At the top you'll see your current status (trial, active, cancelled or locked) and the days you have left. Below are the available plans." },
        { h: "Switching plans (upgrade / downgrade)", p: [
          "Click 'Start subscription' or 'Manage' on the plan you want.",
          "You'll be guided through a secure Stripe checkout — no need to retype anything.",
          "Right after payment the plan is active; your team notices nothing except any lockout is lifted.",
        ]},
        { h: "Invoices & payment method", p: "Use the 'Manage' button to open the Stripe customer portal. There you can download invoices (PDF), update your payment method and see your full billing history." },
        { h: "Cancel or pause", p: "In the Stripe customer portal you can cancel in one click. Access remains until the end of the current period — after that Rostr is locked until you pick a new plan. Your data is safe throughout." },
        { h: "App locked — what does that mean?", p: [
          "When your trial has ended AND there is no active subscription, your managers and employees see a lock screen.",
          "Only you as Eigenaar can still sign in and pick a plan to get everyone back to work.",
          "Data (rosters, leave, hours) stays safely stored during the lock.",
        ]},
      ],
    },
    settings: {
      title: "Company settings",
      subtitle: "Name, timezone, language, privacy and invitation rules",
      articles: [
        { h: "Company details", p: "In Settings → Company you can adjust company name, industry and timezone. These show in the sidebar, in team emails and in exports. Changes apply instantly." },
        { h: "Default language & timezone", p: "New employees automatically get the default language you set here (Dutch or English). Anyone can pick something else later in Profile → Preferences." },
        { h: "Directory privacy", p: [
          "Decide which fields employees see about each other: phone, email, job title, department and birthday.",
          "'Hide birth year' shows only day/month — recommended for privacy.",
          "These rules apply only between employees; management always sees everything.",
        ]},
        { h: "Invitation rules", p: [
          "Decide whether managers and/or team leads can create invite codes themselves.",
          "Set how long codes remain valid (e.g. 14 days) — expired codes stop working.",
          "Codes automatically carry a role: pick employee, team lead or manager when creating one.",
        ]},
      ],
    },
    // --- Management sections ---
    welcome: {
      title: "Getting started",
      subtitle: "From first login to first published roster in 5 minutes",
      articles: [
        { h: "1. Invite your team", p: [
          "Go to Employees → 'Invite code' button.",
          "Pick a role: Employee, Team lead or Manager.",
          "Share the code (WhatsApp, email, break-room note). They enter it at /join, choose a password or sign in with Google, and are active right away.",
          "Codes expire after the number of days you set in Settings.",
        ]},
        { h: "2. Create locations & teams", p: [
          "Locations = physical places (branch, site, project). Each gets a name, address and color — the color shows in the roster.",
          "Teams = groups of employees within your company (e.g. Kitchen, Front-of-house, Sales). Handy for quick filtering and team-based assignment.",
        ]},
        { h: "3. Build your first roster", p: "Open Roster → click an empty cell to assign a shift. Choose an employee directly, or leave open (unassigned) and use 'Quick assign' later." },
        { h: "4. Publish the week", p: "As long as status is 'Draft', nobody sees the roster. Click 'Publish week' — only then do your employees get a push and see their shifts in My roster." },
        { h: "5. Personalize your dashboard", p: "Click 'Customize widgets' at the top-right of the Dashboard. Pick which stats, lists and quick actions you want. Your choice is remembered per user." },
      ],
    },
    dashboard: {
      title: "Dashboard",
      subtitle: "Your control panel — 25+ widgets you choose and rearrange",
      articles: [
        { h: "Add or remove widgets", p: [
          "Click 'Customize' at the top-right of the dashboard.",
          "Widgets are grouped in seven categories: Key stats, My work, Team, Planning, Requests, Updates and Quick actions.",
          "Tick what you want to see (up to 20 at once). Your personal choice is stored locally — colleagues see their own set.",
          "Click 'Reset to default' to return to the recommended set for your role.",
        ]},
        { h: "Key-stat widgets", p: [
          "Employees — total number of active accounts.",
          "Shifts this week — all planned shifts for the next 7 days.",
          "Open shifts — shifts without an assigned employee.",
          "Leave / Swaps / Hours pending — how many requests are waiting for your approval.",
          "Locations and Teams — quick counters.",
          "Total hours week — all planned hours summed up.",
        ]},
        { h: "Team widgets", p: [
          "Team today — who is working now and at what times.",
          "Birthdays — who has a birthday this week (with quick celebrate button).",
          "Top hours per employee — top 5 with most hours this week.",
        ]},
        { h: "Manager-specific widgets", p: [
          "Open-shift alert — open shifts within 48 hours. Red under 24 hours.",
          "Publish status — what percentage of your week is already published, per day.",
          "Roster horizon — how many days ahead your roster reaches (a good long-term check).",
          "Leave taken this year — top 5 employees with most leave days.",
          "Broadcast composer — send a notification straight from your dashboard.",
        ]},
        { h: "Drag & rearrange", p: "On desktop you can drag widgets to change the order. They remember their position for next time." },
      ],
    },
    roster: {
      title: "Build the roster",
      subtitle: "The main planner: week, day and per-employee views",
      articles: [
        { h: "Switch views", p: [
          "By day — chronological view with all shifts of a single day.",
          "By week — the classic matrix (employee × weekdays). Ideal for overview.",
          "By employee — one row per person for quick comparison.",
          "Switch with the tabs on top; your choice is remembered.",
        ]},
        { h: "Add a shift", p: [
          "Click an empty cell or use 'Add shift' at the top-right.",
          "Fill in: date, start and end time, employee (or leave open), location, team, role and optional note.",
          "The note is visible to the employee on their shift — perfect for briefings, tasks or extra info.",
        ]},
        { h: "Open shifts (unassigned)", p: [
          "Leave the 'Employee' field empty — the shift shows as 'Open' on the roster.",
          "Open shifts also appear in 'Quick assign' (mobile) and the 'Open shifts' widget.",
          "Employees CANNOT claim open shifts themselves — you as manager assign them.",
        ]},
        { h: "Copy & paste", p: [
          "Copy previous week — duplicates everything from last week (as draft) into this week.",
          "Copy to next week — mirror this week forward.",
          "Pick days — select specific source days and one or more target days (e.g. Mon → Tue+Wed+Thu).",
          "Keep assigned employees or leave the target days empty. Overlapping shifts are skipped with a popup summary.",
        ]},
        { h: "Duplicate shift (+1 day)", p: "Click a shift → 'Duplicate +1 day'. Creates a copy on the next day with the same times and assignment. Handy for recurring morning shifts." },
        { h: "Publish", p: [
          "While a shift is 'Draft', the employee sees nothing — feel free to shuffle.",
          "Set a shift's status to 'Published' or use 'Publish week' at the top to push everything live at once.",
          "On publish, involved employees get push- and email notifications (depending on their preferences).",
        ]},
        { h: "Using filters", p: "Filter by location or team via the buttons top-right. Great for keeping oversight when planning multiple branches at once." },
      ],
    },
    employees: {
      title: "Employees",
      subtitle: "Manage the team, view profiles and invite",
      articles: [
        { h: "Overview", p: "Sidebar → Employees shows everyone in your company. Search by name, job title or department. Click a row to open the profile." },
        { h: "Inviting", p: [
          "Top-right 'Invite code' → pick the role (Employee, Team lead or Manager).",
          "The code is copied; share via WhatsApp, email or print it.",
          "The person opens /join, enters the code, picks a password or Google login, and is part of your company right away.",
        ]},
        { h: "Profile detail", p: [
          "Overview — contact, role, birthday, upcoming shifts.",
          "Leave — all their leave requests (status + decisions).",
          "Swaps — all sent and received swap requests.",
          "Only you as management can edit the details; employees have a read-only profile.",
        ]},
        { h: "What can you change?", p: [
          "Name, job title, department, phone, birthday.",
          "Location and team assignment.",
          "Role (Employee / Team lead / Manager) — except your own and the Eigenaar's.",
          "Language and notification preferences on the employee's request.",
        ]},
        { h: "Roles & permissions", p: [
          "Employee → own roster, leave, swaps and availability only.",
          "Team lead → same as employee + approve leave/swaps/hours and view reports.",
          "Manager → everything a team lead can + invite/edit employees + build the roster.",
          "Eigenaar → everything + subscription and company settings.",
        ]},
      ],
    },
    locations: {
      title: "Locations",
      subtitle: "Branches, colors and addresses",
      articles: [
        { h: "Why locations?", p: "Every shift is linked to a location. That way the employee knows where they're expected and you can easily filter and report per branch." },
        { h: "Add a location", p: [
          "Sidebar → Locations → 'Add location' button.",
          "Fill in: name (e.g. 'Amsterdam Center'), address, city and a color.",
          "The color appears as an accent on the roster — handy to visually tell branches apart.",
        ]},
        { h: "Assigning employees to a location", p: "In the employee profile pick a primary location. We use it as a suggestion for new shifts (but you can always override)." },
      ],
    },
    teams: {
      title: "Teams",
      subtitle: "Groups of employees for quick filtering",
      articles: [
        { h: "What is a team?", p: "A team is a logical group inside your company: Kitchen, Front-of-house, Register, IT, Sales… One employee can belong to one team." },
        { h: "Add a team", p: "Sidebar → Teams → 'Add team'. Give it a name and optionally a color and description. Then attach employees from their profile or via the team list." },
        { h: "Team filters in the roster", p: "In the Roster use the team filter to see only e.g. 'Front-of-house' — ideal for big teams where multiple departments work at once." },
      ],
    },
    requests: {
      title: "Approve requests",
      subtitle: "Leave, shift swaps and hours",
      articles: [
        { h: "Leave requests", p: [
          "Sidebar → Leave requests → tab 'Waiting' shows everything still to decide.",
          "Click 'Approve' or 'Reject'. Optionally add a note — only you and the requester see it.",
          "On approval, any planned shifts in that period are automatically removed (or flagged for replanning).",
          "The employee gets a push- and email notification immediately.",
        ]},
        { h: "Reviewing shift swaps", p: [
          "Employee A asks B to take their shift → B accepts or rejects.",
          "Once B accepts, the swap reaches you with status 'Accepted — waiting for manager'.",
          "Click 'Approve' → the shift is automatically reassigned to B and both get a confirmation.",
          "You can also reject; the original shift stays with A.",
        ]},
        { h: "Approve hours", p: [
          "Employees submit their actual hours worked (e.g. stayed a bit longer, left earlier).",
          "Sidebar → Approve hours shows the delta vs the roster in amber/red.",
          "Approve to finalize the hours; they show up in your hours report for payroll.",
          "On reject you can ask for a correction — the employee gets a notification.",
        ]},
        { h: "Mobile Manager tools", p: "On your phone the bottom-nav 'Requests' tab lets you approve leave, swaps and hours from one tabbed screen. Perfect on the go." },
      ],
    },
    reports: {
      title: "Reports & CSV export",
      subtitle: "For payroll, HR and analysis",
      articles: [
        { h: "What can you export?", p: [
          "Hours report — all submitted/approved hours per employee in a period.",
          "Roster report — all planned shifts with employee, location and team.",
          "More exports (leave, swap requests) are coming in future updates.",
        ]},
        { h: "Download a report", p: [
          "Sidebar → Reports → pick report type.",
          "Select a period (this week, last week, this month or custom).",
          "Click 'Download CSV' — the file opens straight in Excel or Google Sheets.",
          "Columns are ready for your payroll administration.",
        ]},
      ],
    },
    news: {
      title: "News feed",
      subtitle: "Company-wide announcements and birthdays",
      articles: [
        { h: "When to post?", p: "News is visible to everyone. Use it for opening-hour changes, team events, staff announcements or holidays. Employees get a push and see it at the top of their news feed." },
        { h: "Writing a post", p: [
          "Sidebar → News → 'New post'.",
          "Title + message (rich text).",
          "'Pin to top' — for critical updates that should stand out longer.",
          "You can edit or delete your own posts later; the Eigenaar can also edit others' posts.",
        ]},
        { h: "Birthdays", p: [
          "Rostr automatically shows who is having a birthday today and who has one this month (depending on privacy settings).",
          "Colleagues can hit a 'Celebrate' button on the birthday — that person gets a personal congratulation notification.",
        ]},
        { h: "Swiped-away posts", p: "Employees can hide posts for themselves by swiping left. Their swipe stays private — the post remains visible to everyone else." },
      ],
    },
    notifications: {
      title: "Send & receive notifications",
      subtitle: "Push, email and in-app in one place",
      articles: [
        { h: "Send a broadcast yourself", p: [
          "Sidebar → Notifications → 'Send notification'.",
          "Pick the audience: everyone, or per role (e.g. only team leads).",
          "Title + message + optional 'Also email' checkbox.",
          "Everyone in the audience gets the notification immediately on every device where they're signed in.",
        ]},
        { h: "Automatic notifications", p: [
          "Roster published or your shift changed.",
          "Leave request decided.",
          "Swap request received / approved / rejected.",
          "Role mismatch on assignment (management only).",
          "A colleague's birthday (can be turned off).",
        ]},
        { h: "Setting channels", p: "Each user picks in Profile → Preferences which category comes via which channel (app, email, push). Push also works when the app is closed, provided it's added to the home screen (PWA)." },
      ],
    },
    "mobile-tools": {
      title: "Manager tools on mobile",
      subtitle: "What you can handle on the go from your phone",
      articles: [
        { h: "Team today", p: [
          "Bottom-nav → 'Today'.",
          "Shows three groups: who's working now, who arrives later today and who's already done.",
          "Ideal for a quick check during a break or on your way to the shop.",
        ]},
        { h: "Quick assign", p: [
          "Bottom-nav → 'Assign'.",
          "Shows all open shifts for the next 7 days.",
          "Tap a shift → pick an employee from the list with availability check ('Free' / 'Overlap' / 'Different role').",
          "Assignment is immediate — the employee gets a notification.",
        ]},
        { h: "Requests (mobile)", p: "Bottom-nav → 'Requests' — leave, swap requests and hours approval in one tabbed screen. Perfect for handling on the fly." },
        { h: "What is desktop only?", p: "Building the roster and big edits (bulk-copy, matrix view, exports, company settings, invites) belong on the website — that's where you get oversight and speed." },
      ],
    },
    "my-work": {
      title: "My own work",
      subtitle: "Yes, as manager you also plan your own shifts",
      articles: [
        { h: "'My work' in the sidebar", p: [
          "Under 'My work' you'll find exactly the same tools your employees have:",
          "My roster — your own shifts.",
          "My leave — request leave yourself too (your manager or the Eigenaar approves).",
          "My swaps — swap with a colleague just like anyone else.",
          "Availability — let the team know when you can't work.",
        ]},
      ],
    },
    profile: {
      title: "Profile & preferences",
      subtitle: "Your account, language and theme",
      articles: [
        { h: "Personal details", p: "Under your name in the sidebar → Profile. Here you adjust phone, job title, department and password." },
        { h: "Preferences", p: [
          "Language — pick from 2 languages; switches the whole screen instantly.",
          "Theme — light or dark (just for you).",
          "Notifications — flip channels per category; test with 'Test notification' to see if push works.",
        ]},
      ],
    },
    // --- Employee sections ---
    "my-roster": {
      title: "My roster",
      subtitle: "Your shifts for the coming weeks",
      articles: [
        { h: "What do you see?", p: "All published shifts assigned to you. Every shift shows date, time, location and (sometimes) a note from your manager with extra info or tasks." },
        { h: "Today & upcoming days", p: "The top card shows today's shift with exact times and who you're working with. Below that, your upcoming shifts for this week." },
        { h: "No shifts?", p: "Then your week isn't published yet OR you're not scheduled. Talk to your manager if you have questions." },
      ],
    },
    leave: {
      title: "Request leave",
      subtitle: "Vacation, sick or other absence",
      articles: [
        { h: "How to request leave", p: [
          "Go to My leave → 'Request leave' button.",
          "Pick type: Vacation, Sick, Personal, Unpaid or Other.",
          "Pick start and end date. Days are calculated automatically.",
          "Add a short reason (optional). Your manager can add a note to the decision.",
        ]},
        { h: "Understanding statuses", p: [
          "Pending — your manager hasn't decided yet. You can cancel yourself.",
          "Approved — you're free on those days; scheduled shifts are reviewed by your manager.",
          "Rejected — your manager explains via a note (or ask in person for clarification).",
        ]},
        { h: "Dots on the tabs", p: "The counters above the tabs show new decisions you haven't seen. Tap a tab and they clear — so you know exactly where something new is." },
      ],
    },
    swaps: {
      title: "Swap shifts",
      subtitle: "Swap a shift with a colleague",
      articles: [
        { h: "Sending a swap request", p: [
          "From My roster, tap a shift → 'Swap'.",
          "Pick a specific colleague or leave open so everyone sees the request.",
          "Add a short message (e.g. 'Family visit — can someone take Friday morning?').",
          "Your request stays 'Pending' until someone accepts.",
        ]},
        { h: "Responding to a request", p: "My swap requests → tab 'Received'. Tap Accept or Reject. If you accept, the shift only truly moves to you once your manager approves it." },
        { h: "Understanding statuses", p: [
          "Pending — no colleague has responded yet.",
          "Accepted — colleague agreed, waiting for manager approval.",
          "Approved — the shift officially moved.",
          "Rejected / Cancelled — the request didn't go through.",
        ]},
      ],
    },
    availability: {
      title: "Availability",
      subtitle: "When do you not want or cannot work?",
      articles: [
        { h: "Why fill this in?", p: "Your manager uses your availability to schedule you smarter. For example: 'never Monday morning' or 'weekends only after 6 PM'." },
        { h: "How do you set it?", p: "Go to My availability → tick per weekday when you're free (green check) or not (red cross). Your manager sees this straight away while planning." },
        { h: "One-off exceptions", p: "For one-off exceptions (e.g. a doctor's appointment next week) it's better to talk to your manager or request leave. Availability is for structural patterns." },
      ],
    },
    // Note: employee submit-hours section removed by product decision.
    faq: {
      title: "Frequently asked questions",
      subtitle: "The answers people need most",
      articles: [
        { h: "I'm not getting notifications — now what?", p: [
          "Check in Profile → Preferences that push/email is on for the right category.",
          "iPhone/iPad: add Rostr to your home screen (Share → Add to Home Screen) and allow notifications when it first opens.",
          "Check your system settings (Settings → Notifications → Rostr).",
          "Test with the 'Test notification' button in Preferences.",
        ]},
        { h: "I don't see any shifts — how come?", p: "Shifts still on 'Draft' are invisible to employees. Ask your manager to publish the week." },
        { h: "I requested the wrong leave by accident", p: "As long as the request is 'Pending' you can cancel it via My leave. Already approved? Ask your manager to change it." },
        { h: "Can I install Rostr on my phone?", p: "Yes — Rostr is a PWA. iPhone: Safari → Share → Add to Home Screen. Android: Chrome offers 'Add to Home Screen' automatically." },
        { h: "Where do I email if there's a problem?", p: "All roles can email rosterhelp@gmail.com for personal support. Add your company name and a short description so we can help quickly." },
      ],
    },
  },

};

// Merge Dutch source sections with a translation map for the current language.
// Returns a new sections array with title/subtitle/articles overridden when a
// translation exists for that section id. Icons and IDs are preserved.
export function translateSections(sections, lang) {
  const map = HANDBOOK_TRANSLATIONS[lang];
  if (!map) return sections;
  return sections.map((sec) => {
    const tr = map[sec.id];
    if (!tr) return sec;
    return {
      ...sec,
      title: tr.title ?? sec.title,
      subtitle: tr.subtitle ?? sec.subtitle,
      articles: tr.articles ?? sec.articles,
    };
  });
}
